/**
 * Scrubber markers — the one place that turns chat moments and AI flags into
 * the little pins under the player. Pure so the transport can render them
 * without knowing anything about the chat's data sources.
 */
import { isMediaAnchor, type MediaMessageAnchor, type ReviewThreadEntry } from '@discovr/types';

import { formatTimecode } from '../../../../../lib/media/timecode';
import { messagePreviewText } from '../../../../../lib/messagePreview';
import type { AiFlag } from './aiFlags';

/** Longest snippet the marker tooltip shows before it is cut off. */
export const MARKER_SNIPPET_CHARS = 62;
const ELLIPSIS = '…';
const TITLE_SEPARATOR = ' · ';
const FRAME_SUFFIX = 'frame';
const AI_AUTHOR = 'AI flag';
/** Who a moment belongs to when the sender has neither a name nor an org. */
const UNNAMED_AUTHOR = 'Reviewer';
const MIN_LEFT_PCT = 0;
const MAX_LEFT_PCT = 100;

/** Which cut the markers are for, and how long it runs. */
export interface MarkerScope {
  /** Only moments made on this content version are marked. */
  versionId: string;
  durationMs: number;
}

/** A chat entry as the markers read it — an optimistic one may carry its own frame before the upload lands. */
export type MarkerSourceEntry = ReviewThreadEntry & { local_frame_url?: string };

/** A chat entry known to carry a media anchor. */
type MomentEntry = MarkerSourceEntry & { anchor: MediaMessageAnchor };

export interface Marker {
  id: string;
  kind: 'note' | 'ai';
  tMs: number;
  /** Horizontal position along the scrubber, 0..100. */
  leftPct: number;
  /** Who the mark belongs to — the screen-reader label needs it on its own. */
  author: string;
  title: string;
  snippet: string;
  /** The note's captured frame (markup baked in) — what its hover thumbnail shows. */
  frameFileId?: string;
  /** An optimistic note's own frame, shown before its upload gives the anchor a file id. */
  localFrameUrl?: string;
}

function leftPctFor(tMs: number, durationMs: number): number {
  // Before `loadedmetadata` the duration is 0/NaN — park everything at the start.
  if (!Number.isFinite(durationMs) || durationMs <= 0) return MIN_LEFT_PCT;
  const pct = (tMs / durationMs) * MAX_LEFT_PCT;
  return Math.min(MAX_LEFT_PCT, Math.max(MIN_LEFT_PCT, pct));
}

function truncate(text: string): string {
  if (text.length <= MARKER_SNIPPET_CHARS) return text;
  return `${text.slice(0, MARKER_SNIPPET_CHARS)}${ELLIPSIS}`;
}

function titleFor(tMs: number, author: string, hasFrame: boolean): string {
  const parts = [formatTimecode(tMs), author];
  if (hasFrame) parts.push(FRAME_SUFFIX);
  return parts.join(TITLE_SEPARATOR);
}

function authorOf(entry: ReviewThreadEntry): string {
  const name = entry.sender_name?.trim();
  if (name) return name;
  const org = entry.sender_title?.trim();
  if (org) return org;
  return UNNAMED_AUTHOR;
}

function snippetOf(entry: MomentEntry): string {
  return truncate(messagePreviewText(entry).trim());
}

function isMomentOn(versionId: string) {
  return function isCurrentMoment(entry: MarkerSourceEntry): entry is MomentEntry {
    return isMediaAnchor(entry.anchor) && entry.anchor.content_version_id === versionId;
  };
}

function noteMarker(entry: MomentEntry, durationMs: number): Marker {
  const tMs = entry.anchor.t_ms;
  const author = authorOf(entry);
  return {
    id: entry.id,
    kind: 'note',
    tMs,
    leftPct: leftPctFor(tMs, durationMs),
    author,
    title: titleFor(tMs, author, !!entry.anchor.frame),
    snippet: snippetOf(entry),
    frameFileId: entry.anchor.frame?.file_id,
    localFrameUrl: entry.local_frame_url,
  };
}

function aiMarker(flag: AiFlag, durationMs: number): Marker {
  return {
    id: flag.id,
    kind: 'ai',
    tMs: flag.tMs,
    leftPct: leftPctFor(flag.tMs, durationMs),
    author: AI_AUTHOR,
    title: titleFor(flag.tMs, AI_AUTHOR, false),
    snippet: truncate(flag.label.trim()),
  };
}

/** The chat's moments on the current cut and AI flags as one time-ordered set of scrubber markers. */
export function buildMarkers(entries: MarkerSourceEntry[], aiFlags: AiFlag[], { versionId, durationMs }: MarkerScope): Marker[] {
  const moments = entries.filter(isMomentOn(versionId));
  const markers = [
    ...moments.map((entry) => noteMarker(entry, durationMs)),
    ...aiFlags.map((flag) => aiMarker(flag, durationMs)),
  ];
  return markers.sort((a, b) => a.tMs - b.tMs);
}
